import React, { FC, useEffect, useRef, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { getPrimaryTintSurface } from '@modernloop/shared/theme';
import { Box, Stack, Theme, Typography } from '@mui/material';

import { SourceType } from 'src/generated/mloop-graphql';

import { useAiAgentLabel } from 'src/entities/ActivityLog/Helpers/useAiAgentLabel';
import TaskTimelineActivityLogsList from 'src/entities/ActivityLogsList/TaskTimelineActivityLogsList';
import { shouldSafetyPollAgentActivityLogs } from 'src/entities/ActivityLogsList/agentActivityLogSafetyPoll';
import SubscribersSelect from 'src/entities/SubscribersSelect';

import { useTaskTimelineActivityFeedRefetch } from 'src/hooks/useActivityFeedRefetch';
import useIsAiTaskEnabled from 'src/hooks/useIsAiTaskEnabled';

import ActivityFeedAgentStatus from './ActivityFeedAgentStatus';
import ActivityFeedComposer from './ActivityFeedComposer';
import { ActivityFeedTabs, getActivityTypesForActivityFeedTab } from './activityFeedFilters';

const NEAR_BOTTOM_THRESHOLD_PX = 80;

const isNearScrollBottom = (element: HTMLElement) =>
  element.scrollHeight - element.scrollTop - element.clientHeight < NEAR_BOTTOM_THRESHOLD_PX;

type Props = {
  taskId: string;
  aiEnabled?: boolean;
  delegateStatus?: string | null;
  onHasUnsavedNoteChangesChange?: (value: boolean) => void;
  onNoteCreated?: () => void;
};

/**
 * Renders the task timeline as a bottom-up activity feed with a sticky composer.
 */
const BottomUpTaskTimelineFeed: FC<Props> = ({
  taskId,
  aiEnabled = false,
  delegateStatus,
  onHasUnsavedNoteChangesChange,
  onNoteCreated,
}) => {
  const { t } = useTranslation();
  const aiTaskEnabled = useIsAiTaskEnabled();
  const agentFeaturesEnabled = aiEnabled && aiTaskEnabled;
  const aiAgentLabel = useAiAgentLabel();
  const refetchData = useTaskTimelineActivityFeedRefetch();
  const safetyPollActivityLogs = shouldSafetyPollAgentActivityLogs(agentFeaturesEnabled, delegateStatus);
  const scrollRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef(true);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect manages an external scroll container after render.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return undefined;
    }
    const updateAutoScroll = () => {
      shouldAutoScrollRef.current = isNearScrollBottom(el);
    };

    let animationFrameId: number | null = null;
    const scrollToBottom = () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
      animationFrameId = requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    };
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            if (!shouldAutoScrollRef.current) {
              return;
            }
            scrollToBottom();
          });
    const observeScrollableContent = () => {
      resizeObserver?.observe(el);
      Array.from(el.children).forEach((child) => resizeObserver?.observe(child));
    };
    const mutationObserver = new MutationObserver(() => {
      observeScrollableContent();
      if (!shouldAutoScrollRef.current) {
        return;
      }
      scrollToBottom();
    });

    observeScrollableContent();
    mutationObserver.observe(el, { childList: true, subtree: true });
    el.addEventListener('scroll', updateAutoScroll);
    el.scrollTop = el.scrollHeight;
    updateAutoScroll();
    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
      el.removeEventListener('scroll', updateAutoScroll);
    };
  }, []);

  const handleNoteChange = (value: string) => {
    if (value && !hasUnsavedChanges) {
      setHasUnsavedChanges(true);
      if (onHasUnsavedNoteChangesChange) {
        onHasUnsavedNoteChangesChange(true);
      }
    } else if (!value && hasUnsavedChanges) {
      setHasUnsavedChanges(false);
      if (onHasUnsavedNoteChangesChange) {
        onHasUnsavedNoteChangesChange(false);
      }
    }
  };

  const onCreateNoteSubmit = () => {
    refetchData();
    if (onNoteCreated) {
      onNoteCreated();
    }
  };

  const agentStatusSlot = agentFeaturesEnabled ? (
    <Box
      sx={{
        minHeight: 22,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
      }}
    >
      <ActivityFeedAgentStatus
        sourceId={taskId}
        sourceType={SourceType.SourceTypeTask}
        delegateStatus={delegateStatus}
        aiAgentLabel={aiAgentLabel}
      />
    </Box>
  ) : null;

  return (
    <Stack
      sx={{
        width: '50%',
        minWidth: 320,
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        ...(agentFeaturesEnabled && {
          background: (theme: Theme) =>
            `linear-gradient(90deg, ${getPrimaryTintSurface(theme)} 0%, ${getPrimaryTintSurface(theme)} 100%), ${theme.palette.background.paper}`,
        }),
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        px={2.5}
        py={1.5}
        flexShrink={0}
        sx={{
          backdropFilter: 'blur(10px)',
          ...(agentFeaturesEnabled && {
            background: (theme: Theme) =>
              `linear-gradient(90deg, ${getPrimaryTintSurface(theme)} 0%, ${getPrimaryTintSurface(theme)} 100%), ${theme.palette.background.paper}`,
          }),
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} fontSize={15}>
          {t('hiring-dashboard-tasks:tasks.timeline')}
        </Typography>
        <SubscribersSelect sourceId={taskId} sourceType={SourceType.SourceTypeTask} showSubscribeButton={false} />
      </Stack>

      <Stack
        ref={scrollRef}
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          overflowAnchor: 'none',
          minHeight: 0,
          minWidth: 0,
          px: 2.5,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box flexGrow={1} flexShrink={1} flexBasis={0} />
        <TaskTimelineActivityLogsList
          taskId={taskId}
          activityTypes={getActivityTypesForActivityFeedTab(ActivityFeedTabs.All)}
          safetyPollActivityLogs={safetyPollActivityLogs}
        />
      </Stack>

      <Stack
        sx={{
          flexShrink: 0,
          backdropFilter: 'blur(10px)',
          ...(agentFeaturesEnabled && {
            background: (theme: Theme) =>
              `linear-gradient(90deg, ${getPrimaryTintSurface(theme)} 0%, ${getPrimaryTintSurface(theme)} 100%), ${theme.palette.background.paper}`,
          }),
          px: 2.5,
          pb: 2.5,
        }}
      >
        <ActivityFeedComposer
          sourceId={taskId}
          sourceType={SourceType.SourceTypeTask}
          onSubmit={onCreateNoteSubmit}
          onChange={handleNoteChange}
          timeline
          aiEnabled={agentFeaturesEnabled}
        />
        {agentStatusSlot}
      </Stack>
    </Stack>
  );
};

export default BottomUpTaskTimelineFeed;
